// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { InstrumentUtils } from '../../Utils/Instruments/InstrumentUtils';
import { ProductType } from '../../Utils/Instruments/ProductType';
import { QuoteValid } from '../../Utils/Quotes/QuoteValid';
import { DateTimeConvertor } from '../../Utils/Time/DateTimeConvertor';
import { OperationType } from '../../Utils/Trading/OperationType';
import { DataCache } from '../DataCache';
import { type Account } from './Account';
import { type Instrument } from './Instrument';

export class PositionsBalance {
    public rowID = null;

    public Instrument: Instrument;
    public Position: any;

    public Symbol: any;

    public NetPositionQty: number = null; // always in lots
    public BreakEvenPoint: number = null;
    public CurrentPrice = null;
    public GrossPL = null;
    public LongQty = null; // 5
    public AverageLong = null;
    public ShortQty = null;
    public AverageShort = null;
    public GrossExposure = null;
    public NetExposure = null; // 10
    public StrikePrice = null;
    public ExpDate = null;
    public PositionValue = null;

    public isCorporateAction: boolean;

    public isEmpty: boolean;

    public Account: Account | null;
    public InstrumentStr: string | null;

    private readonly _accsToUse: any;

    constructor (position, accsToUse) {
        if (!position) {
            return;
        }

        const ins = position.Instrument;
        if (!ins) return;

        this.Instrument = ins;
        this.Position = position;

        this.Symbol = position.GetInstrumentDisplayName(); // zero column

        this.isCorporateAction = position.ProductType === ProductType.CorporateAction;

        this._accsToUse = accsToUse;

        this.isEmpty = accsToUse ? this.RecalculateByAccounts(accsToUse) : this.RecalculateByInstrument(ins);
    }

    public RecalculateByAccounts (accsInUse): boolean {
        return this.RecalculateByInstrument(this.Instrument, accsInUse);
    }

    public RecalculateByInstrument (ins: Instrument, accToUse?, aggregateTradableID?): boolean {
        const UseAccCurrency = DataCache.getUserBaseCurrency() !== DataCache.baseCurrency;

        let profit = 0;
        let exposure = 0; let exposureNet = 0;
        let positionValue = 0;
        let everBuy = 0; let everSell = 0;
        let buyAmount = 0; let sellAmount = 0;
        let isEmpty = true;
        let amount = 0;

        const tID = InstrumentUtils.RemoveRouteName(ins.GetInteriorID());
        const routeID = ins.getRoute();
        const routesArr = aggregateTradableID ? ins.RoutesSupported : [routeID];
        let exposurePart = 1;

        for (let i = 0; i < routesArr.length; i++) {
            const instrument = aggregateTradableID ? DataCache.getInstrumentByName(InstrumentUtils.GetFullName(tID, routesArr[i])) : ins;
            const positions = DataCache.getPositionsByInstrument(instrument, this.isCorporateAction);

            for (const posId in positions) {
                const pos = positions[posId];
                const isBuy = pos.BuySell == OperationType.Buy;

                if (pos.isTradePosition || (accToUse && !accToUse[pos.Account.AcctNumber])) {
                    continue;
                }

                const tickCost = this.Instrument.GetTickCost();
                const posExpositionValue = pos.getExpositionValue(UseAccCurrency);
                exposurePart = UseAccCurrency ? pos.OpenCrossPrice : DataCache.CrossRateCache.GetCrossPriceIns(this.Instrument) * tickCost;

                isEmpty = false;
                profit += pos.getProfitValue(UseAccCurrency);
                exposure += posExpositionValue;
                positionValue += pos.getPositionValue(UseAccCurrency);
                amount = pos.Amount; // ALWAYS IN LOTS

                if (isBuy) {
                    buyAmount += amount;
                    everBuy += pos.Price * amount;
                    exposureNet += posExpositionValue / exposurePart;
                } else {
                    sellAmount += amount;
                    everSell += pos.Price * amount;
                    exposureNet -= posExpositionValue / exposurePart;
                }
            }
        }

        this.NetPositionQty = buyAmount - sellAmount;
        this.AverageLong = (buyAmount > 0) ? everBuy / buyAmount : 0;
        this.AverageShort = (sellAmount > 0) ? everSell / sellAmount : 0;

        this.BreakEvenPoint = (this.NetPositionQty != 0) ? (buyAmount * this.AverageLong - sellAmount * this.AverageShort) / (this.NetPositionQty) : -1; // #43665
        if (this.BreakEvenPoint < 0 && this.BreakEvenPoint != -1) {
            this.BreakEvenPoint = -1;
        } // признак невалидного значения

        this.CurrentPrice = PositionsBalance.GetCurrentPrice(ins, buyAmount, sellAmount);
        this.GrossPL = isNaN(this.CurrentPrice) ? NaN : profit;
        this.LongQty = buyAmount || 0;
        this.ShortQty = sellAmount || 0;
        this.NetExposure = exposureNet * exposurePart;
        this.GrossExposure = exposure;
        this.StrikePrice = (ins != null && ins.StrikePrice > 0) ? ins.StrikePrice : 0;
        this.ExpDate = (ins?.ExpDate) ? DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(new Date(ins.ExpDateReal.getTime())) : null;
        this.PositionValue = positionValue;

        return isEmpty;
    }

    public static GetCurrentPrice (instrument: Instrument | null, buyAmount, sellAmount): number {
        if (instrument?.DataCache == null) {
            return -1;
        }

        if (instrument.CalcPriceOnTrade()) {
            const lastPrice = instrument.Level1.GetLastPrice();
            if (isNaN(lastPrice) || lastPrice >= 0) {
                return lastPrice;
            }

            return -1;
        } else {
            const lastQuote = instrument.GetLastQuote(QuoteValid.Last);
            if (lastQuote == null) {
                return -1;
            }

            let lastPrice = 0;

            if (!lastQuote.IsValid) {
                lastPrice = instrument.Level1.GetLastPrice();
            }

            const sp = instrument.DataCache.GetSpreadPlan();
            const ask = !isNaN(lastQuote.Ask) ? lastQuote.AskSpread_SP_Ins(sp, instrument) : lastPrice;
            const bid = !isNaN(lastQuote.Bid) ? lastQuote.BidSpread_SP_Ins(sp, instrument) : lastPrice;

            // чтобы было как в позиции (#39817)
            let curPrice = (buyAmount - sellAmount > 0) ? bid : ask;
            if (curPrice == 0) {
                const prevClose = instrument.Level1.GetPrevClose();
                if (prevClose >= 0) {
                    curPrice = prevClose;
                }
            }

            return curPrice;
        }
    }

    public Dispose (): void {
        this.Account = null;
        this.InstrumentStr = null;
    }

    public Recalc (): void {
        this.isEmpty = this._accsToUse ? this.RecalculateByAccounts(this._accsToUse) : this.RecalculateByInstrument(this.Instrument);
    }
}
